import {
  Experiments,
  PackagePickerType,
  PlanType,
  RealEstateId,
  Step,
  Vertical,
} from 'enums';
import { AppStore } from 'stores/App.store';
import { ParsedAppIntentElements } from 'types';
import { shouldShowDomain, shouldShowPremiumRequiredForDomain } from 'utils';

interface PageTitleKeys {
  main: string;
  secondUpgrade?: string;
  anonymous?: string;
  specialDiscount?: string;
  intents?: {
    bookings?: string;
    acceptPayments?: string;
    enterprise?: string;
    fitness?: string;
    website?: string;
    blog?: string;
    domain?: string;
  };
}

interface TitleKeys {
  changePlan: string;
  plansPage: PageTitleKeys;
  subscriptionsPage: PageTitleKeys;
  checkoutPage: PageTitleKeys;
}

const MAIN_TITLE_KEYS: TitleKeys = {
  changePlan: 'change_plan.app_title',
  plansPage: {
    main: 'plans_page.business_title',
    secondUpgrade: 'plans_page.second_upgrade_title',
    anonymous: 'premium-purchase-plan.anonymous.header.title',
    intents: {
      bookings: 'plans_page.booking_title',
      acceptPayments: 'plans_page.payments_title',
      enterprise: 'enterprise_plans.page.title',
      website: 'plans_page.title',
      fitness: 'plans_page_fitness_title',
      blog: 'plans_page.blogs_title',
      domain: 'plans_page.domain_intent_title',
    },
  },
  subscriptionsPage: { main: 'subscription_page.advanced_plans_title' },
  checkoutPage: { main: 'checkout.title' },
};

const DAYFUL_TITLE_KEYS: TitleKeys = {
  changePlan: 'change_plan.app_title',
  plansPage: { main: 'plans_page.dayful_title' },
  subscriptionsPage: { main: 'plans_page.dayful_title' },
  checkoutPage: { main: 'plans_page.dayful_title' },
};

const EDITOR_X_TITLE_KEYS: TitleKeys = {
  ...MAIN_TITLE_KEYS,
  plansPage: {
    ...MAIN_TITLE_KEYS.plansPage,
    specialDiscount: 'Advanced_Plans_special_discount_title',
  },
};

const SUNRISE_TITLE_KEYS: TitleKeys = {
  ...MAIN_TITLE_KEYS,
  plansPage: {
    intents: {
      ...MAIN_TITLE_KEYS.plansPage.intents,
      acceptPayments: 'premium-purchase-plan.header.default.title',
    },
    anonymous: 'premium-purchase-plan.anonymous.header.title',
    main: 'premium-purchase-plan.header.default.title',
    secondUpgrade: 'premium-purchase-plan.header.second_upgrade.title',
  },
};

const STUDIO_TITLE_KEYS: TitleKeys = {
  ...MAIN_TITLE_KEYS,
  plansPage: {
    main: 'studio_package_picker.header.default.title',
    anonymous: 'studio_package_picker.anon.main_header',
  },
};

const pageSubtitleMapV1 = {
  DEFAULT: 'app.subtitleText',
  DAYFUL: 'plans_page.dayful_subtitle',
  SECOND_UPGRADE: 'app.second_upgrade_text',
  CHANGE_PLAN: 'change_plan.app_sub_title',
  ENTERPRISE: 'enterprise_plans.page.subtitle',
  SPECIAL_DISCOUNT: {
    0: 'Advanced_Plans_plans_page_special_discount_subtitle',
    1: 'Advanced_Plans_subscription_page_special_discount_subtitle',
    2: 'Advanced_Plans_checkout_page_special_discount_subtitle',
  },
  SEPA: 'app.sepaSubtitleText',
};

const getPlansPageTitle = (
  appStore: AppStore,
  pageTitleKeys: PageTitleKeys,
): string | undefined => {
  const {
    planType,
    isBusinessVertical,
    appContext,
    isSecondUpgrade,
    isEditorX,
    isEditorXSpecialDiscountEnabled,
    isAnonymous,
  } = appStore;

  const vertical = appContext.verticalName;
  // editorx uses the second upgrade title for website and business
  // but in default flow website is getting the default title.

  if (isAnonymous) {
    return pageTitleKeys.anonymous;
  }
  if (isEditorXSpecialDiscountEnabled) {
    return pageTitleKeys.specialDiscount;
  }
  if (isSecondUpgrade && isEditorX) {
    return pageTitleKeys.secondUpgrade;
  }
  if (planType === PlanType.Website) {
    return pageTitleKeys.intents?.website;
  }
  if (planType === PlanType.Enterprise) {
    return pageTitleKeys.intents?.enterprise;
  }
  if (vertical === Vertical.Bookings) {
    return pageTitleKeys.intents?.bookings;
  }
  if (isBusinessVertical) {
    return pageTitleKeys.intents?.acceptPayments;
  }
  if (isSecondUpgrade) {
    return pageTitleKeys.secondUpgrade;
  }
  return pageTitleKeys.main;
};

const getProductPageTitleFromDealerOffers = (appStore: AppStore): string => {
  const { isSecondUpgrade, planType, appContext } = appStore;
  const { parsedDealerOffers } = appContext;

  const appTitleByTab = (
    isSecondUpgrade
      ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
      : parsedDealerOffers.get(RealEstateId.AppIntentElementsInitialPurchase)
  ) as ParsedAppIntentElements;

  return appTitleByTab[planType];
};

interface AppTitles {
  appTitle: string;
  appSubtitle: string;
}

export const useAppTitles = (appStore: AppStore): AppTitles => {
  const {
    currentStep,
    isChangePlan,
    isSecondUpgrade,
    planType,
    isUseDealerIntegrationIntentElementsEnabled,
    isEditorXSpecialDiscountEnabled,
    isDayful,
    isSepaCountry,
    isAnonymous,
    appContext: { packagePickerType, experiments },
  } = appStore;

  let pageTitleKeys = MAIN_TITLE_KEYS,
    pageSubtitleKeys = pageSubtitleMapV1;
  if (
    packagePickerType === PackagePickerType.Studio ||
    packagePickerType === PackagePickerType.EditorXToStudio
  ) {
    pageTitleKeys = STUDIO_TITLE_KEYS;
    pageSubtitleKeys = {} as any;
  } else if (packagePickerType === PackagePickerType.Sunrise) {
    pageTitleKeys = SUNRISE_TITLE_KEYS;
    pageSubtitleKeys = {} as any;
  } else if (packagePickerType === PackagePickerType.Dayful) {
    pageTitleKeys = DAYFUL_TITLE_KEYS;
  } else if (packagePickerType === PackagePickerType.EditorX) {
    pageTitleKeys = EDITOR_X_TITLE_KEYS;
  }

  const getAppTitle = () => {
    if (isChangePlan) {
      return pageTitleKeys.changePlan;
    }

    if (currentStep === Step.Plans) {
      if (
        packagePickerType === PackagePickerType.Sunrise &&
        experiments[Experiments.EnableImmediateCouponsDeliverySpec] === 'true'
      ) {
        return 'premium-purchase-plan.header.title.biggest_sale_of_the_year';
      }

      if (
        packagePickerType === PackagePickerType.Sunrise &&
        shouldShowPremiumRequiredForDomain(experiments)
      ) {
        return 'sunrise_package_picker.title.go_live_with_domain';
      }

      if (shouldShowDomain(experiments)) {
        return 'premium-purchase-plan.header.domain.title';
      }

      if (isUseDealerIntegrationIntentElementsEnabled && !isAnonymous) {
        return getProductPageTitleFromDealerOffers(appStore);
      }
      return (
        getPlansPageTitle(appStore, pageTitleKeys.plansPage) ||
        pageTitleKeys.plansPage.main
      );
    }

    if (currentStep === Step.Subscriptions) {
      return pageTitleKeys.subscriptionsPage.main;
    }

    return pageTitleKeys.checkoutPage.main;
  };

  const getAppSubtitle = () => {
    if (isDayful) {
      return pageSubtitleKeys?.DAYFUL;
    }
    if (isAnonymous) {
      return 'premium-purchase-plan.anonymous.header.subtitle';
    }
    if (isSepaCountry) {
      return pageSubtitleKeys?.SEPA;
    }
    if (isEditorXSpecialDiscountEnabled) {
      return pageSubtitleKeys?.SPECIAL_DISCOUNT[currentStep];
    }
    if (isSecondUpgrade) {
      return pageSubtitleKeys?.SECOND_UPGRADE;
    }
    if (isChangePlan) {
      return pageSubtitleKeys?.CHANGE_PLAN;
    }
    if (planType === PlanType.Enterprise) {
      return pageSubtitleKeys?.ENTERPRISE;
    }
    return pageSubtitleKeys?.DEFAULT;
  };
  const appTitle = getAppTitle();
  const appSubtitle = getAppSubtitle();

  return {
    appTitle,
    appSubtitle,
  };
};
